import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Phone } from "lucide-react";
import Navbar from "./Navbar";
import FloatingButtons from "./FloatingButtons";
import Footer from "./Footer";

const IndustryPageTemplate = ({ data }) => {
  const featuresRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (featuresRef.current) observer.observe(featuresRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-[#050505] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section
        data-testid="industry-page-hero"
        className="relative min-h-[60vh] flex items-end pb-16 pt-28 overflow-hidden"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={data.image}
            alt={data.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/60 to-transparent" />
        </div>

        {/* Background orbs */}
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(0,229,153,0.1) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <Link
            to="/"
            data-testid="industry-page-back-btn"
            className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={15} />
            Back to Home
          </Link>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00E599]/25 bg-[#00E599]/5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E599]" />
            <span className="text-xs font-bold text-[#00E599] tracking-widest uppercase">
              {data.badge}
            </span>
          </div>

          <h1
            data-testid="industry-page-title"
            className="text-5xl md:text-6xl font-black text-white mb-4"
            style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.03em" }}
          >
            {data.title}
          </h1>
          <p className="text-xl text-[#A1A1AA] max-w-2xl leading-relaxed mb-8">
            {data.tagline}
          </p>

          <a
            href="#contact"
            data-testid="industry-page-cta"
            onClick={() => {
              window.location.href = "/#contact";
            }}
            className="inline-flex items-center gap-2 bg-[#00E599] text-black font-semibold rounded-full px-8 py-4 hover:bg-[#00ff99] transition-all duration-200 hover:shadow-[0_0_25px_rgba(0,229,153,0.35)]"
          >
            {data.cta}
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Stats Row */}
      <section className="bg-[#0A0A0A] border-y border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {data.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div
                  className="text-3xl font-bold text-[#00E599] mb-1"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {stat.number}
                </div>
                <div className="text-xs text-[#71717A]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section
        ref={featuresRef}
        className="py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Problems */}
            <div
              className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              <h2
                className="text-3xl font-bold text-white mb-6"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Problems We Solve
              </h2>
              <div className="space-y-4">
                {data.problems.map((p, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-[#0A0A0A] border border-white/5">
                    <div className="w-8 h-8 rounded-lg bg-red-500/8 border border-red-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-red-400 text-xs font-bold">{i + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-sm mb-1">{p.title}</h4>
                      <p className="text-xs text-[#71717A]">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Solutions */}
            <div
              className={`transition-all duration-700 delay-150 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              <h2
                className="text-3xl font-bold text-white mb-6"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Our AI Solutions
              </h2>
              <div className="space-y-4">
                {data.solutions.map((s, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-[#0A0A0A] border border-[#00E599]/8">
                    <div className="w-8 h-8 rounded-lg bg-[#00E599]/8 border border-[#00E599]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 size={15} className="text-[#00E599]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-sm mb-1">{s.title}</h4>
                      <p className="text-xs text-[#71717A]">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Outcome Box */}
          <div className="mt-12 p-8 rounded-2xl bg-[#00E599]/4 border border-[#00E599]/15">
            <h3
              className="text-xl font-bold text-[#00E599] mb-3"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Expected Outcomes
            </h3>
            <p className="text-[#A1A1AA] leading-relaxed">{data.outcome}</p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Ready to Transform Your {data.industry}?
          </h2>
          <p className="text-[#A1A1AA] mb-8 max-w-xl mx-auto">
            Book a free strategy session with our AI experts and get a custom roadmap for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+919958891551"
              data-testid="industry-page-call-btn"
              className="inline-flex items-center gap-2 bg-[#00E599] text-black font-semibold rounded-full px-8 py-4 hover:bg-[#00ff99] transition-all"
            >
              <Phone size={15} />
              Call +91 99588 91551
            </a>
            <a
              href="https://wa.me/919958891551?text=Hi%20CodeIntel%20AI%20Labs,%20I%20am%20interested%20in%20AI%20automation,%20AI%20agents,%20or%20a%20custom%20AI%20solution%20for%20my%20business.%20Let's%20discuss."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="industry-page-whatsapp-btn"
              className="inline-flex items-center gap-2 border border-white/15 text-white font-semibold rounded-full px-8 py-4 hover:bg-white/5 transition-all"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default IndustryPageTemplate;
